/* eslint-disable ts/member-ordering */
import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import axios from 'axios';
import type { User } from '@/scenes/authentication/types/types.auth.js';
import { trackEvent } from '@/third-party/ga.js';
import * as Sentry from '@sentry/react';
import { log } from '../utils/log';

const UserPoolData = {
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
};

const userPool = new CognitoUserPool(UserPoolData);

class AuthService {
  // Track pending refresh to prevent race conditions
  private pendingRefresh: null | Promise<CognitoUserSession> = null;

  private getCognitoUser(email: string): CognitoUser {
    return new CognitoUser({
      Pool: userPool,
      Username: email,
    });
  }

  /**
   * Chokepoint: Always called when a user is successfully authenticated/logged in by any method (including email, SSO, and post-signup).
   * @param user
   * @param method
   */
  public authenticationSuccessful(user: User, method: 'email' | 'sso' = 'email') {
    trackEvent({
      eventCategory: method,
      eventLabel: user.email,
      eventName: 'login_success',
    });

    // Update Sentry user context while preserving the session continuity
    // This links the anonymous session with the authenticated user
    Sentry.setUser({
      email: user.email,
      id: user.id,
    });
    Sentry.setTag('session_type', 'authenticated');
    Sentry.setTag('auth_method', method);

    // if user createdAt is within 30 seconds of now, track it as a sign-up
    if (user.createdAt) {
      const createdAt = new Date(user.createdAt);
      const now = new Date();
      if (now.getTime() - createdAt.getTime() < 60_000) {
        trackEvent({
          eventCategory: method,
          eventLabel: user.email,
          eventName: 'sign_up',
        });
        Sentry.setTag('sign_up', 'true');
        Sentry.setTag('sign_up_method', method);
      }
    }
  }

  public async setCognitoSessionFromSSO({
    accessToken,
    email,
    idToken,
    refreshToken,
  }: {
    accessToken: string;
    email: string;
    idToken: string;
    refreshToken: string;
  }): Promise<User> {
    try {
      const cognitoUser = this.getCognitoUser(email);

      const session = new CognitoUserSession({
        AccessToken: new CognitoAccessToken({ AccessToken: accessToken }),
        IdToken: new CognitoIdToken({ IdToken: idToken }),
        RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshToken }),
      });

      // Set session in memory
      cognitoUser.setSignInUserSession(session);

      const keyPrefix = `CognitoIdentityServiceProvider.${UserPoolData.ClientId}.${email}`;

      // Persist session tokens
      localStorage.setItem(`CognitoIdentityServiceProvider.${UserPoolData.ClientId}.LastAuthUser`, email);
      localStorage.setItem(`${keyPrefix}.idToken`, idToken);
      localStorage.setItem(`${keyPrefix}.accessToken`, accessToken);
      localStorage.setItem(`${keyPrefix}.refreshToken`, refreshToken);

      const user = await this.buildUser(email, session);
      this.authenticationSuccessful(user, 'sso');
      return user;
    } catch (error) {
      log.error('Error setting Cognito session from SSO:', error);
      throw error;
    }
  }

  public refreshSession(): Promise<CognitoUserSession> {
    // If there's already a refresh in progress, return that promise
    // This prevents race conditions when multiple components try to refresh simultaneously
    if (this.pendingRefresh) {
      return this.pendingRefresh;
    }

    this.pendingRefresh = new Promise<CognitoUserSession>((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No authenticated user.'));
        return;
      }

      cognitoUser.getSession((error: Error | null, session: CognitoUserSession | null) => {
        if (error) {
          log.error('[AuthService] Error getting session:', error);
          reject(error);
        } else if (session) {
          if (session.isValid()) {
            resolve(session);
          } else {
            cognitoUser.refreshSession(session.getRefreshToken(), (refreshError, newSession) => {
              if (refreshError) {
                log.error('[AuthService] Error refreshing session:', refreshError);
                reject(refreshError);
              } else {
                resolve(newSession);
              }
            });
          }
        } else {
          reject(new Error('No session found.'));
        }
      });
    }).finally(() => {
      // Clear the pending refresh once complete (success or failure)
      this.pendingRefresh = null;
    });

    return this.pendingRefresh;
  }

  private async fetchUserDetails(email: string, token: string) {
    if (!email) {
      return;
    }
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/user/${email}`, {
        headers: {
          Authorization: token,
        },
      });
      return response.data;
    } catch (error: any) {
      log.error('❌ Error fetching user details:', error);
      throw error;
    }
  }

  private async buildUser(email: string, session: CognitoUserSession): Promise<User> {
    const idToken = session.getIdToken().getJwtToken();
    const payload = session.getIdToken().decodePayload?.() ?? session.getIdToken().payload;
    const isAdmin = payload['cognito:groups']?.includes('admin') || false;

    try {
      const userDetails = await this.fetchUserDetails(email, idToken);
      return {
        ...userDetails,
        hasCompletedRegistration: !!userDetails,
        idToken,
        isAdmin,
      };
    } catch (error) {
      log.error('Error fetching user details:', error);
      return {
        email,
        hasCompletedRegistration: false,
        idToken,
        isAdmin,
      };
    }
  }

  public authenticateUser(email: string, password: string): Promise<{ newPasswordRequired: true } | User> {
    return new Promise((resolve, reject) => {
      const authDetails = new AuthenticationDetails({
        Password: password,
        Username: email,
      });

      const cognitoUser = this.getCognitoUser(email);
      cognitoUser.authenticateUser(authDetails, {
        newPasswordRequired: () => {
          return resolve({ newPasswordRequired: true });
        },
        onFailure: (error) => {
          return reject(error);
        },
        onSuccess: async (session: CognitoUserSession) => {
          const user = await this.buildUser(email, session);
          this.authenticationSuccessful(user);
          resolve(user);
        },
      });
    });
  }

  public confirmRegistration(email: string, code: string, forceAliasCreation: boolean): Promise<string> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCognitoUser(email);
      cognitoUser.confirmRegistration(code, forceAliasCreation, (error, result) => {
        if (error) {
          reject(error);
        } else {
          trackEvent({ eventName: 'signup_email_verified' });
          resolve(result);
        }
      });
    });
  }

  public resendConfirmationCode(email: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCognitoUser(email);
      cognitoUser.resendConfirmationCode((error, result) => {
        if (error) {
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  public changePassword(oldPassword: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.getSession((error: Error | null, session: CognitoUserSession | null) => {
          if (error) {
            reject(error);
          } else if (session) {
            cognitoUser.changePassword(oldPassword, newPassword, (error) => {
              if (error) {
                log.error('Error changing password:', error);
                reject(error);
              } else {
                resolve();
              }
            });
          } else {
            reject(new Error('No active session'));
          }
        });
      } else {
        reject(new Error('No authenticated user'));
      }
    });
  }

  public completeNewPasswordChallenge(email: string, newPassword: string, requiredAttributes: any): Promise<User> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCognitoUser(email);
      cognitoUser.completeNewPasswordChallenge(newPassword, requiredAttributes, {
        onFailure: (error: any) => {
          reject(error);
        },
        onSuccess: (session: CognitoUserSession) => {
          this.buildUser(email, session)
            .then((user) => {
              this.authenticationSuccessful(user);
              resolve(user);
              return user;
            })
            .catch((err) => {
              reject(err);
            });
        },
      });
    });
  }

  public signUpUser(email: string, password: string): Promise<any> {
    return new Promise((resolve, reject) => {
      userPool.signUp(email, password, [], [], (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
    });
  }

  /**
   * Clears Cognito session and storage tokens, signing out the current user.
   */
  public logout(): void {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    const lastUserKey = `CognitoIdentityServiceProvider.${UserPoolData.ClientId}.LastAuthUser`;
    const keyPrefix = `CognitoIdentityServiceProvider.${UserPoolData.ClientId}.${localStorage.getItem(lastUserKey)}`;
    localStorage.removeItem(lastUserKey);
    localStorage.removeItem(`${keyPrefix}.idToken`);
    localStorage.removeItem(`${keyPrefix}.accessToken`);
    localStorage.removeItem(`${keyPrefix}.refreshToken`);
    // Clear session storage to prevent stale data from previous user
    sessionStorage.clear();
    // Optionally clear axios auth header
    delete axios.defaults.headers.common['Authorization'];
  }

  public async getCurrentUser(): Promise<null | User> {
    return await new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        return resolve(null);
      }

      cognitoUser.getSession(async (error: Error | null, session: CognitoUserSession | null) => {
        if (error || !session || !session.isValid()) {
          return reject(error || new Error('Invalid session'));
        }

        const idToken: string = session.getIdToken().getJwtToken();
        const payload: Record<string, any> = session.getIdToken().decodePayload?.() ?? session.getIdToken().payload;

        const email: string = payload.email;
        const isAdmin: boolean = payload['cognito:groups']?.includes('admin') || false;

        if (!email) {
          log.error('Email missing from ID token');
          return reject(new Error('Email not found in token'));
        }

        try {
          const userDetails = await this.fetchUserDetails(email, idToken);

          const user: User = {
            email,
            idToken,
            isAdmin,
            ...userDetails,
            hasCompletedRegistration: true,
          };
          this.authenticationSuccessful(user);
          resolve(user);
        } catch {
          // User not in DB yet - use fallback profile
          const fallbackUser = {
            company: '',
            companySize: '',
            email,
            firstName: '',
            followedCompanies: [],
            hasCompletedRegistration: false,
            idToken,
            industry: '',
            isAdmin,
            lastName: '',
            onboardingConfig: [],
            positionTitle: '',
            reviews: [],
          };
          resolve(fallbackUser);
        }
      });
    });
  }

  public async handleOAuthCallback(accessToken: string, idToken: string, refreshToken: string): Promise<User> {
    try {
      // Decode the ID token to get user email
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const email = payload.email;

      if (!email) {
        throw new Error('Email not found in token');
      }

      // Set the Cognito session
      const user = await this.setCognitoSessionFromSSO({
        accessToken,
        email,
        idToken,
        refreshToken,
      });

      return user;
    } catch (error) {
      log.error('Error handling OAuth callback:', error);
      throw error;
    }
  }
}

export const authService = new AuthService();
